(() => {
  'use strict';

  const SELECTOR = '[data-hero-cinematic]';

  class HeroCinematic {
    constructor(element) {
      this.element = element;
      this.media = element.querySelector('.hero-cinematic__media');
      this.product = element.querySelector(
        '.hero-cinematic__product-stage'
      );

      this.pointerX = 0;
      this.pointerY = 0;
      this.currentX = 0;
      this.currentY = 0;
      this.frameId = null;
      this.isVisible = true;

      this.reducedMotion = window.matchMedia(
        '(prefers-reduced-motion: reduce)'
      ).matches;

      this.isFinePointer = window.matchMedia(
        '(hover: hover) and (pointer: fine)'
      ).matches;

      this.handlePointerMove =
        this.handlePointerMove.bind(this);

      this.handlePointerLeave =
        this.handlePointerLeave.bind(this);

      this.handleScroll =
        this.handleScroll.bind(this);

      this.animate =
        this.animate.bind(this);

      this.init();
    }

    init() {
      if (!this.media) return;

      this.observeVisibility();

      if (!this.reducedMotion) {
        window.addEventListener(
          'scroll',
          this.handleScroll,
          { passive: true }
        );

        this.handleScroll();
      }

      if (!this.reducedMotion && this.isFinePointer) {
        this.media.addEventListener(
          'pointermove',
          this.handlePointerMove
        );

        this.media.addEventListener(
          'pointerenter',
          () => {
            this.element.classList.add('is-interacting');
          }
        );

        this.media.addEventListener(
          'pointerleave',
          this.handlePointerLeave
        );

        this.frameId = requestAnimationFrame(this.animate);
      }
    }

    observeVisibility() {
      if (!('IntersectionObserver' in window)) {
        this.isVisible = true;
        return;
      }

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            this.isVisible = entry.isIntersecting;

            this.element.classList.toggle(
              'is-visible',
              entry.isIntersecting
            );
          });
        },
        {
          threshold: 0.08
        }
      );

      observer.observe(this.element);
      this.observer = observer;
    }

    handlePointerMove(event) {
      const bounds = this.media.getBoundingClientRect();

      if (!bounds.width || !bounds.height) return;

      const normalizedX =
        (event.clientX - bounds.left) / bounds.width - 0.5;

      const normalizedY =
        (event.clientY - bounds.top) / bounds.height - 0.5;

      this.pointerX = normalizedX;
      this.pointerY = normalizedY;
    }

    handlePointerLeave() {
      this.pointerX = 0;
      this.pointerY = 0;

      this.element.classList.remove('is-interacting');
    }

    handleScroll() {
      if (!this.isVisible) return;

      const bounds = this.element.getBoundingClientRect();
      const viewportHeight =
        window.innerHeight || document.documentElement.clientHeight;

      const progress =
        (viewportHeight - bounds.top) /
        (viewportHeight + bounds.height);

      const limitedProgress = Math.min(
        1,
        Math.max(0, progress)
      );

      const scrollOffset =
        (limitedProgress - 0.5) * 18;

      this.element.style.setProperty(
        '--hero-scroll-y',
        `${scrollOffset.toFixed(2)}px`
      );
    }

    animate() {
      if (this.isVisible) {
        const easing = 0.075;

        this.currentX +=
          (this.pointerX - this.currentX) * easing;

        this.currentY +=
          (this.pointerY - this.currentY) * easing;

        const productX = this.currentX * 15;
        const productY = this.currentY * 10;

        const backgroundX = this.currentX * -7;
        const backgroundY = this.currentY * -5;

        this.element.style.setProperty(
          '--hero-parallax-x',
          `${productX.toFixed(2)}px`
        );

        this.element.style.setProperty(
          '--hero-parallax-y',
          `${productY.toFixed(2)}px`
        );

        this.element.style.setProperty(
          '--hero-bg-x',
          `${backgroundX.toFixed(2)}px`
        );

        this.element.style.setProperty(
          '--hero-bg-y',
          `${backgroundY.toFixed(2)}px`
        );
      }

      this.frameId = requestAnimationFrame(this.animate);
    }

    destroy() {
      window.removeEventListener(
        'scroll',
        this.handleScroll
      );

      if (this.media) {
        this.media.removeEventListener(
          'pointermove',
          this.handlePointerMove
        );

        this.media.removeEventListener(
          'pointerleave',
          this.handlePointerLeave
        );
      }

      if (this.frameId) {
        cancelAnimationFrame(this.frameId);
      }

      if (this.observer) {
        this.observer.disconnect();
      }
    }
  }

  const instances = new WeakMap();

  function initializeHero(element) {
    if (!element || instances.has(element)) return;

    const instance = new HeroCinematic(element);
    instances.set(element, instance);
  }

  function initializeAll(scope = document) {
    scope.querySelectorAll(SELECTOR).forEach(initializeHero);
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => initializeAll()
    );
  } else {
    initializeAll();
  }

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initializeAll(event.target);
    }
  );

  document.addEventListener(
    'shopify:section:unload',
    (event) => {
      const hero = event.target.querySelector(SELECTOR);

      if (!hero) return;

      const instance = instances.get(hero);

      if (instance) {
        instance.destroy();
        instances.delete(hero);
      }
    }
  );
})();